import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ChangeDetectorRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../../services/api.service';
import { NotificationService } from '../../../../services/notification.service'; // NotificationService;
import {
  createModalLogoUploadConfig,
  getDisplayNameForLogo,
  handleModalLogoUpload,
  removeModalLogo,
  revokeBlobUrl,
} from '../campaign-logo-upload.util';
import { buildSmsCampaignSuccessPageUrl } from '../campaign-platform-url.util';
import { replaceSuccessPageMergeFields } from '../success-page-merge-fields.util';

export interface SuccessPageModalData {
  reuseLandingBrand: boolean;
  logoUrl?: string;
  title: string;
  body: string;
  showCta: boolean;
  ctaText: string;
  ctaUrl: string;
  ctaColor: string;
  showResend: boolean;
  maxAttempts: number;
  cooldownText: string;
}

export const SUCCESS_PAGE_PLACEHOLDERS: SuccessPageModalData = {
  reuseLandingBrand: false,
  title: "You're in",
  body: "Check your phone — we've sent your code to {customer contact}. Show it at any participating way location.",
  showCta: false,
  ctaText: 'Visit our website',
  ctaUrl: 'https://way.com',
  ctaColor: '#2F6DDE',
  showResend: true,
  maxAttempts: 3,
  cooldownText: 'Resend available in 30s',
};

export const DEFAULT_SUCCESS_PAGE_DATA: SuccessPageModalData = { ...SUCCESS_PAGE_PLACEHOLDERS };

const DEFAULT_SUCCESS_VALIDATION_ERRORS = {
  logo: '',
  title: '',
  body: '',
  ctaText: '',
  ctaUrl: '',
  maxAttempts: '',
};

const EMPTY_FORM_DATA: SuccessPageModalData = {
  reuseLandingBrand: false,
  title: '',
  body: '',
  showCta: false,
  ctaText: '',
  ctaUrl: '',
  ctaColor: SUCCESS_PAGE_PLACEHOLDERS.ctaColor,
  showResend: true,
  maxAttempts: SUCCESS_PAGE_PLACEHOLDERS.maxAttempts,
  cooldownText: '',
};

@Component({
  selector: 'app-create-modal-success-page',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './create-modal-success-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './create-modal-success-page.component.scss',
})
export class CreateModalSuccessPageComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() initialData: SuccessPageModalData | null = null;
  @Input() campaignId: string | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<SuccessPageModalData>();

  formData: SuccessPageModalData = { ...EMPTY_FORM_DATA };
  readonly placeholders = SUCCESS_PAGE_PLACEHOLDERS;
  previewMode: 'desktop' | 'mobile' = 'desktop';
  logoFile: File | null = null;
  logoBlobUrl = '';
  logoFileName = '';
  isUploadingLogo = false;
  fileValidationError = '';
  validationErrors = { ...DEFAULT_SUCCESS_VALIDATION_ERRORS };
  readonly maxLogoDimensions = { width: 1000, height: 1000 };
  readonly maxLogoFileSizeBytes = 2 * 1024 * 1024;
  readonly maxResendAttempts = 10;
  readonly availableBodyTags = ['customer contact'];
  readonly buttonColors = [
    '#2F6DDE',
    '#15172E',
    '#22C55E',
    '#EAB308',
    '#06B6D4',
    '#EC4899',
    '#4F46E5',
    '#14B8A6',
    '#F97316',
    '#A855F7',
    '#8B5CF6',
    '#7C3AED',
  ];

  private apiService = inject(ApiService);
  private messageService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['isOpen'] && this.isOpen) || changes['initialData']) {
      this.populateFormFields();
    }
  }

  populateFormFields(): void {
    if (this.initialData?.title?.trim()) {
      this.formData = { ...this.initialData };
      if (this.initialData.logoUrl) {
        this.logoFileName = getDisplayNameForLogo(this.initialData.logoUrl);
      }
    } else {
      this.formData = { ...EMPTY_FORM_DATA };
      this.clearLogo();
    }
    this.previewMode = 'desktop';
    this.clearValidationErrors();
    this.fileValidationError = '';
  }

  ngOnDestroy(): void {
    this.revokeLogoBlobUrl();
  }

  get logoPreviewUrl(): string {
    if (this.formData.reuseLandingBrand) {
      return '';
    }
    return this.logoBlobUrl || this.formData.logoUrl || '';
  }

  onClose(): void {
    this.revokeLogoBlobUrl();
    this.close.emit();
  }

  onReset(): void {
    this.formData = { ...EMPTY_FORM_DATA };
    this.clearValidationErrors();
    this.clearLogo();
  }

  onSave(): void {
    if (!this.validateForm()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Complete success page details!',
        detail: 'Fill in the empty fields to save.',
      });
      return;
    }
    this.applyOptionalPlaceholderDefaults();
    this.save.emit({
      ...this.formData,
      logoUrl: this.formData.reuseLandingBrand ? '' : this.formData.logoUrl || '',
    });
    this.releaseLocalLogoPreview();
  }

  validateForm(): boolean {
    this.clearValidationErrors();

    let isValid = true;

    if (!this.formData.reuseLandingBrand && !this.formData.logoUrl?.trim() && !this.logoBlobUrl) {
      this.validationErrors.logo = 'Logo is required';
      isValid = false;
    }

    if (!this.formData.title?.trim()) {
      this.validationErrors.title = 'Title is required';
      isValid = false;
    }

    if (!this.formData.body?.trim()) {
      this.validationErrors.body = 'Body is required';
      isValid = false;
    }

    if (this.formData.showCta) {
      if (!this.formData.ctaText?.trim()) {
        this.validationErrors.ctaText = 'Button label is required';
        isValid = false;
      }
      const ctaUrl = this.formData.ctaUrl?.trim();
      if (!ctaUrl) {
        this.validationErrors.ctaUrl = 'Button URL is required';
        isValid = false;
      } else if (!this.isValidUrl(ctaUrl)) {
        this.validationErrors.ctaUrl = 'Enter a valid URL starting with http:// or https://';
        isValid = false;
      }
    }

    if (this.formData.showResend) {
      const maxAttempts = Number(this.formData.maxAttempts);
      if (!maxAttempts || maxAttempts < 1) {
        this.validationErrors.maxAttempts = 'Max attempts must be at least 1';
        isValid = false;
      } else if (maxAttempts > this.maxResendAttempts) {
        this.validationErrors.maxAttempts = `Max attempts cannot exceed ${this.maxResendAttempts}`;
        isValid = false;
      }
    }

    if (this.isUploadingLogo) {
      isValid = false;
    }

    return isValid;
  }

  clearValidationErrors(): void {
    this.validationErrors = { ...DEFAULT_SUCCESS_VALIDATION_ERRORS };
  }

  /** Fills empty optional resend fields from placeholders before save. */
  private applyOptionalPlaceholderDefaults(): void {
    const p = SUCCESS_PAGE_PLACEHOLDERS;
    if (!this.formData.cooldownText?.trim()) {
      this.formData.cooldownText = p.cooldownText;
    }
    if (!this.formData.maxAttempts || this.formData.maxAttempts < 1) {
      this.formData.maxAttempts = p.maxAttempts;
    } else if (this.formData.maxAttempts > this.maxResendAttempts) {
      this.formData.maxAttempts = this.maxResendAttempts;
    }
  }

  onMaxAttemptsChange(): void {
    const maxAttempts = Number(this.formData.maxAttempts);
    if (
      this.validationErrors.maxAttempts &&
      maxAttempts >= 1 &&
      maxAttempts <= this.maxResendAttempts
    ) {
      this.validationErrors.maxAttempts = '';
    }
  }

  onTitleChange(): void {
    if (this.validationErrors.title && this.formData.title?.trim()) {
      this.validationErrors.title = '';
    }
  }

  onBodyChange(): void {
    if (this.validationErrors.body && this.formData.body?.trim()) {
      this.validationErrors.body = '';
    }
  }

  onCtaTextChange(): void {
    if (this.validationErrors.ctaText && this.formData.ctaText?.trim()) {
      this.validationErrors.ctaText = '';
    }
  }

  onCtaUrlChange(): void {
    const ctaUrl = this.formData.ctaUrl?.trim();
    if (this.validationErrors.ctaUrl && ctaUrl && this.isValidUrl(ctaUrl)) {
      this.validationErrors.ctaUrl = '';
    }
  }

  onColorSelect(color: string): void {
    this.formData.ctaColor = color;
  }

  private isValidUrl(value: string): boolean {
    try {
      const parsed = new URL(value);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }

  getPreviewCtaText(): string {
    return this.formData.ctaText?.trim() || this.placeholders.ctaText;
  }

  insertBodyTag(tag: string): void {
    this.formData.body = `${this.formData.body || ''}{${tag}}`;
    this.onBodyChange();
  }

  trackByIndex(index: number): number {
    return index;
  }

  private getFormDataWithPlaceholders(): SuccessPageModalData {
    const p = SUCCESS_PAGE_PLACEHOLDERS;
    return {
      ...this.formData,
      title: this.formData.title?.trim() || p.title,
      body: this.formData.body?.trim() || p.body,
      cooldownText: this.formData.cooldownText?.trim() || p.cooldownText,
      maxAttempts: this.formData.maxAttempts > 0 ? this.formData.maxAttempts : p.maxAttempts,
    };
  }

  onReuseLandingBrandChange(): void {
    if (this.formData.reuseLandingBrand) {
      this.clearLogo();
      this.validationErrors.logo = '';
    }
    this.cdr.detectChanges();
  }

  setPreviewMode(mode: 'desktop' | 'mobile'): void {
    this.previewMode = mode;
  }

  onViewInBrowser(): void {
    const url = buildSmsCampaignSuccessPageUrl(this.campaignId);
    if (!url) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Success page unavailable',
        detail: 'Save the campaign first to preview the success page.',
      });
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  getPreviewText(text: string): string {
    return replaceSuccessPageMergeFields(text, '{customer contact}');
  }

  getResendStatusText(): string {
    const resolved = this.getFormDataWithPlaceholders();
    const triesLabel = resolved.maxAttempts === 1 ? 'try' : 'tries';
    return `${resolved.cooldownText} · ${resolved.maxAttempts} ${triesLabel}`;
  }

  getPreviewTitle(): string {
    return this.getPreviewText(this.formData.title?.trim() || this.placeholders.title);
  }

  getPreviewBody(): string {
    return this.getPreviewText(this.formData.body?.trim() || this.placeholders.body);
  }

  async onLogoUpload(event: Event): Promise<void> {
    await handleModalLogoUpload(event, this.buildLogoUploadConfig(), formData =>
      this.apiService.uploadCampaignFile(formData)
    );
  }

  removeLogo(event?: Event): void {
    removeModalLogo(event, () => this.clearLogo());
  }

  private buildLogoUploadConfig() {
    return createModalLogoUploadConfig({
      maxWidth: this.maxLogoDimensions.width,
      maxHeight: this.maxLogoDimensions.height,
      maxBytes: this.maxLogoFileSizeBytes,
      canUpload: () => !this.formData.reuseLandingBrand,
      revokeBlob: () => this.revokeLogoBlobUrl(),
      assignPreparedFile: (file, blobUrl, fileName) => {
        this.logoFile = file;
        this.logoBlobUrl = blobUrl;
        this.logoFileName = fileName;
        this.formData.logoUrl = '';
        this.cdr.detectChanges();
      },
      applyUploadedLogo: logoUrl => {
        this.formData.logoUrl = logoUrl;
        this.logoFileName = getDisplayNameForLogo(logoUrl);
        this.validationErrors.logo = '';
        this.cdr.detectChanges();
      },
      onUploadFailure: () => this.clearLogoStateAfterError(),
      setUploading: uploading => {
        this.isUploadingLogo = uploading;
      },
      setValidationError: message => {
        this.fileValidationError = message;
      },
      detectChanges: () => this.cdr.detectChanges(),
      showUploadErrorToast: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Upload failed',
          detail: 'Failed to upload logo. Please try again.',
        });
      },
    });
  }

  private clearLogoStateAfterError(): void {
    this.logoFile = null;
    this.logoFileName = '';
    this.formData.logoUrl = '';
    this.releaseLocalLogoPreview();
  }

  private releaseLocalLogoPreview(): void {
    this.logoFile = null;
    this.revokeLogoBlobUrl();
  }

  private clearLogo(): void {
    this.logoFile = null;
    this.formData.logoUrl = '';
    this.logoFileName = '';
    this.fileValidationError = '';
    this.releaseLocalLogoPreview();
  }

  private revokeLogoBlobUrl(): void {
    revokeBlobUrl(this.logoBlobUrl);
    this.logoBlobUrl = '';
  }
}
